import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  EnvelopeSimple,
  Phone,
  MapPin,
  PaperPlaneTilt,
  CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import PageHero from "../components/site/PageHero";
import { COMPANY } from "../data/site";
import { submitContactForm } from "../lib/publicApi";

const subjects = [
  { label: "General Enquiry", value: "GENERAL_ENQUIRY" },
  { label: "Retailer Onboarding", value: "RETAILER_ONBOARDING" },
  { label: "Partner / Distributor", value: "PARTNER_DISTRIBUTOR" },
  { label: "Support / Technical", value: "SUPPORT_TECHNICAL" },
  { label: "Press / Media", value: "PRESS_MEDIA" },
];

const fieldMap = {
  name: "full_name",
  phone: "mobile_number",
  email: "email_address",
  subject: "subject",
  message: "message",
};

const getFieldError = (errors, field) => {
  const error = errors[fieldMap[field]];

  if (Array.isArray(error)) {
    return error.join(" ");
  }

  return error;
};

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "GENERAL_ENQUIRY",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const onChange = (k, v) => {
    setSubmitMessage("");
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[fieldMap[k]];
      return next;
    });
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitMessage("");
    setFieldErrors({});

    try {
      const response = await submitContactForm(form);
      setSubmitMessage(response.message || "");
      setSubmitted(true);
    } catch (error) {
      setSubmitMessage(error.message);
      setFieldErrors(error.errors || {});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get in touch"
        title="Let's talk —"
        highlight="we're listening"
        description="Whether you're a retailer, a distributor, or just curious about JayesPay, our team will get back within one business day."
        testId="contact-hero"
      />

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-7 bg-surfaceAlt border border-line rounded-3xl p-6 sm:p-10"
          >
            {submitted ? (
              <div data-testid="contact-success-state" className="text-center py-14">
                <div className="mx-auto h-20 w-20 rounded-full brand-icon-tile flex items-center justify-center mb-5">
                  <CheckCircle size={40} weight="fill" className="text-ink900" />
                </div>
                <h3 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight mb-2">
                  Thank you — message received!
                </h3>
                <p className="text-ink700 max-w-md mx-auto mb-6">
                  {submitMessage ||
                    `Our team will get back to you at the email you provided. For urgent help, call ${COMPANY.phone}.`}
                </p>
                <Button
                  data-testid="contact-send-another"
                  onClick={() => {
                    setForm({
                      name: "",
                      email: "",
                      phone: "",
                      subject: "GENERAL_ENQUIRY",
                      message: "",
                    });
                    setSubmitMessage("");
                    setFieldErrors({});
                    setSubmitted(false);
                  }}
                  className="bg-brandNavy text-surfaceAlt hover:bg-brandBlue rounded-full px-6"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-infoText mb-2">
                    Drop us a line
                  </div>
                  <h2 className="font-heading font-extrabold text-ink900 text-2xl sm:text-3xl tracking-tight">
                    Tell us how we can help.
                  </h2>
                </div>

                {submitMessage ? (
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                    {submitMessage}
                  </div>
                ) : null}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-ink700 mb-1.5 block">
                      Full name
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      required
                      value={form.name}
                      onChange={(e) => onChange("name", e.target.value)}
                      placeholder="Your full name"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "name") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "name")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-ink700 mb-1.5 block">
                      Mobile number
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => onChange("phone", e.target.value)}
                      placeholder="+91 9xxxx xxxxx"
                      className="bg-surface border-line"
                    />
                    {getFieldError(fieldErrors, "phone") ? (
                      <p className="mt-1.5 text-xs font-medium text-red-600">
                        {getFieldError(fieldErrors, "phone")}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-ink700 mb-1.5 block">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    data-testid="contact-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => onChange("email", e.target.value)}
                    placeholder="you@example.com"
                    className="bg-surface border-line"
                  />
                  {getFieldError(fieldErrors, "email") ? (
                    <p className="mt-1.5 text-xs font-medium text-red-600">
                      {getFieldError(fieldErrors, "email")}
                    </p>
                  ) : null}
                </div>

                <div>
                  <Label htmlFor="subject" className="text-ink700 mb-1.5 block">
                    Subject
                  </Label>
                  <Select
                    value={form.subject}
                    onValueChange={(v) => onChange("subject", v)}
                  >
                    <SelectTrigger
                      id="subject"
                      data-testid="contact-subject"
                      className="bg-surface border-line"
                    >
                      <SelectValue placeholder="Choose a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          data-testid={`subject-${s.value}`}
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {getFieldError(fieldErrors, "subject") ? (
                    <p className="mt-1.5 text-xs font-medium text-red-600">
                      {getFieldError(fieldErrors, "subject")}
                    </p>
                  ) : null}
                </div>

                <div>
                  <Label htmlFor="message" className="text-ink700 mb-1.5 block">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => onChange("message", e.target.value)}
                    placeholder="How can the JayesPay team help you?"
                    className="bg-surface border-line resize-none"
                  />
                  {getFieldError(fieldErrors, "message") ? (
                    <p className="mt-1.5 text-xs font-medium text-red-600">
                      {getFieldError(fieldErrors, "message")}
                    </p>
                  ) : null}
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="btn-shimmer w-full bg-brandNavy text-surfaceAlt hover:bg-brandBlue rounded-full h-12 text-sm font-bold"
                >
                  {loading ? "Sending..." : "Send message"}
                  <PaperPlaneTilt size={16} weight="bold" className="ml-2" />
                </Button>

                <p className="text-xs text-ink500 text-center">
                  By submitting, you agree to our Privacy Policy. We never share
                  your data.
                </p>
              </form>
            )}
          </motion.div>

          {/* Side info */}
          <div className="lg:col-span-5 space-y-4">
            {[
              {
                Icon: EnvelopeSimple,
                title: "Email",
                value: COMPANY.email,
                href: `mailto:${COMPANY.email}`,
                tag: "Replies within 1 business day",
              },
              {
                Icon: Phone,
                title: "Phone",
                value: COMPANY.phone,
                href: `tel:${COMPANY.phone.replace(/\s/g, "")}`,
                tag: "Mon – Sat, 10 AM – 7 PM IST",
              },
              {
                Icon: MapPin,
                title: "Registered Office",
                value: COMPANY.address,
                tag: COMPANY.legalName,
              },
            ].map(({ Icon, title, value, href, tag }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="bg-surfaceAlt border border-line rounded-3xl p-7 hover:border-brandBlue/40 transition-colors"
                data-testid={`contact-info-${title.toLowerCase()}`}
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-brandBlue/15 text-infoText flex items-center justify-center flex-shrink-0">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink500 mb-1">
                      {title}
                    </div>
                    {href ? (
                      <a
                        href={href}
                        className="font-bold text-ink900 hover:text-infoText transition-colors break-words"
                      >
                        {value}
                      </a>
                    ) : (
                      <div className="font-semibold text-ink900 leading-snug">
                        {value}
                      </div>
                    )}
                    <div className="text-xs text-ink500 mt-2">{tag}</div>
                  </div>
                </div>
              </motion.div>
            ))}

            <div className="bg-ink900 text-surface rounded-3xl p-7">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-brandOrange mb-3">
                Company
              </div>
              <div className="font-heading font-bold text-xl tracking-tight mb-1">
                {COMPANY.legalName}
              </div>
              <div className="text-sm text-surface/65">CIN: {COMPANY.cin}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
